import React, { useRef, useState } from 'react';

import { link } from '@aline/routes';
import {
  Badge,
  Button,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Popper,
  Typography,
} from '@mui/material';
import type { GridToolbarContainerProps } from '@mui/x-data-grid-premium';
import { Bot, CheckCheck, Clock, Info, List as ListIcon, Sparkles, Tag, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useClickAway } from 'react-use';
import {
  AlineDialog,
  AlineDialogButtons,
  AlineTooltip,
  DocumentPicker,
  DocumentPickerVariant,
  DropdownSelectButton,
  PrimaryPlusIconButton,
  SearchInput,
  Spacer,
  useListToolbarStyles,
} from 'src/components';
import type { UserOption } from 'src/components/ai/reminder-automation/UserAutocomplete';
import { UserAutocomplete } from 'src/components/ai/reminder-automation/UserAutocomplete';
import { useAccessGuard } from 'src/components/auth';
import { useSelectCurrentUser } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import { doNothing, useDispatchNotification } from 'src/helpers';
import { useCreateReminder } from 'src/hooks';
import { useGlobalDrawer } from 'src/layouts/GlobalRightDrawer';
import { makeStyles } from 'tss-react/mui';

import { ReminderTypeChooser } from './ReminderTypeChooser';

export interface RemindersLocalState {
  filter: Apollo.ReminderFilter;
  filterAssignees: UserOption[];
  filterAutomationId: UUID | null;
  filterReportId: UUID | null;
  filterTypes: Apollo.ReminderType[];
  searchString: string;
}

export interface RemindersToolbarProps extends GridToolbarContainerProps {
  reminderAlertsProps?: ReminderAlertsProps;
  remindersStateTuple?: [
    RemindersLocalState,
    React.Dispatch<React.SetStateAction<RemindersLocalState>>,
  ];
}

const useStyles = makeStyles()(theme => ({
  alertIcon: {
    alignItems: 'center',
    color: theme.appColors.iconsType,
    display: 'flex',
    flexShrink: 0,
    height: 20,
    justifyContent: 'center',
    width: 20,
  },
  alertRow: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    padding: theme.spacing(1.25, 1.5),
  },
  alertRowInner: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minWidth: 0,
    width: 280,
  },
  alertTitle: {
    color: theme.appColors.iconsType,
    fontSize: 14,
    fontWeight: 600,
    lineHeight: '20px',
  },
  alertsButton: {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.appColors.rule}`,
    color: theme.appColors.iconsType,
    height: 32,
    width: 32,
  },
  alertsButtonActive: {
    borderColor: theme.appColors.primary,
    color: theme.appColors.primary,
  },
  assigneesPaper: {
    borderRadius: 8,
    padding: theme.spacing(1),
    width: 320,
  },
  dialogButtons: {
    flexShrink: 0,
    paddingTop: theme.spacing(1),
  },
  dialogPaper: {
    /**
     * Bound dialog to ~75% of viewport height so Cancel/Create are always on
     * screen. The picker inside flexes to fill the remaining space. The
     * `maxHeight` guard matches MUI's default Dialog margin so we never
     * exceed the viewport on very tall screens.
     */
    display: 'flex',
    flexDirection: 'column',
    height: '75vh',
    maxHeight: 'calc(100vh - 64px)',
    maxWidth: 1080,
    width: '90vw',
  },
  dialogPickerWrapper: {
    display: 'flex',
    flex: '1 1 0',
    flexDirection: 'column',
    minHeight: 0,
  },
  menuItemIcon: {
    minWidth: `${theme.spacing(4)} !important`,
  },
  paper: {
    borderRadius: 8,
    width: 420,
  },
  paperHeader: {
    fontWeight: 700,
    padding: theme.spacing(1.5, 1.5, 0.5),
  },
  popper: {
    zIndex: theme.zIndex.tooltip,
  },
  subtitle: {
    color: theme.appColors.secondaryType,
    fontSize: 12,
  },
  title: {
    flexShrink: 0,
    fontWeight: 600,
    marginRight: theme.spacing(1),
  },
  viewAllButton: {
    borderRadius: 999,
    flexShrink: 0,
    textTransform: 'none',
  },
}));

const STATUS_LABEL: Record<Apollo.ReminderFilter, string> = {
  [Apollo.ReminderFilter.All]: 'Any status',
  [Apollo.ReminderFilter.Completed]: 'Completed',
  [Apollo.ReminderFilter.Incomplete]: 'Active',
  [Apollo.ReminderFilter.PendingReview]: 'Pending Review',
};

const STATUS_ICON: Record<Apollo.ReminderFilter, typeof Clock> = {
  [Apollo.ReminderFilter.All]: ListIcon,
  [Apollo.ReminderFilter.Completed]: CheckCheck,
  [Apollo.ReminderFilter.Incomplete]: Clock,
  [Apollo.ReminderFilter.PendingReview]: Sparkles,
};

const STATUS_MENU_OPTIONS: Apollo.ReminderFilter[] = [
  Apollo.ReminderFilter.Incomplete,
  Apollo.ReminderFilter.All,
  Apollo.ReminderFilter.Completed,
  Apollo.ReminderFilter.PendingReview,
];

export function RemindersToolBar({
  reminderAlertsProps,
  remindersStateTuple,
}: RemindersToolbarProps) {
  const { classes } = useStyles();
  const { classes: toolbarClasses } = useListToolbarStyles();
  const [remindersState, setRemindersState] = remindersStateTuple ?? [null, doNothing];
  const hasAiFilter = Boolean(remindersState?.filterAutomationId || remindersState?.filterReportId);
  const statusValue = remindersState?.filter ?? Apollo.ReminderFilter.Incomplete;
  const [statusAnchorEl, setStatusAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);

  const selectStatus = (value: Apollo.ReminderFilter) => {
    setStatusMenuOpen(false);
    setRemindersState(s => ({
      ...s,
      filter: value,
      filterAutomationId: null,
      filterReportId: null,
    }));
  };

  const TriggerIcon = STATUS_ICON[statusValue];

  return (
    <div className={toolbarClasses.toolbar}>
      <Typography className={classes.title} variant="h6">
        Reminders
      </Typography>
      <ReminderTypePicker remindersStateTuple={remindersStateTuple} />
      <RemindersAssigneesPicker remindersStateTuple={remindersStateTuple} />
      {!hasAiFilter && (
        <>
          <DropdownSelectButton
            ref={setStatusAnchorEl}
            icon={<TriggerIcon size={18} />}
            label={STATUS_LABEL[statusValue]}
            selected={statusMenuOpen}
            variant="outlined"
            onClick={() => setStatusMenuOpen(!statusMenuOpen)}
          />
          <Menu
            anchorEl={statusAnchorEl}
            anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
            open={statusMenuOpen}
            transformOrigin={{ horizontal: 'left', vertical: 'top' }}
            onClose={() => setStatusMenuOpen(false)}
          >
            {STATUS_MENU_OPTIONS.map(option => {
              const OptionIcon = STATUS_ICON[option];
              return (
                <MenuItem
                  key={option}
                  selected={statusValue === option}
                  onClick={() => selectStatus(option)}
                >
                  <ListItemIcon className={classes.menuItemIcon}>
                    <OptionIcon size={18} />
                  </ListItemIcon>
                  <ListItemText>{STATUS_LABEL[option]}</ListItemText>
                </MenuItem>
              );
            })}
          </Menu>
        </>
      )}
      <Spacer />
      <SearchInput
        placeholder="Search reminders..."
        search={remindersState?.searchString ?? ''}
        setSearch={searchString => setRemindersState(s => ({ ...s, searchString }))}
      />
      {reminderAlertsProps && <ReminderAlertsButton {...reminderAlertsProps} />}
      <AddReminder />
    </div>
  );
}

export interface ReminderAlertsProps {
  isPendingReviewFilterActive: boolean;
  onClearPendingReviewFilter: () => void;
  onViewPendingReview: () => void;
  pendingReviewCount: number;
}

function ReminderAlertsButton({
  isPendingReviewFilterActive,
  onClearPendingReviewFilter,
  onViewPendingReview,
  pendingReviewCount,
}: ReminderAlertsProps): JSX.Element | null {
  const { classes, cx } = useStyles();
  const anchorRef = useRef<HTMLDivElement>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [popperOpen, setPopperOpen] = useState(false);

  useClickAway(anchorRef, () => setPopperOpen(false));

  if (pendingReviewCount === 0) return null;

  const handleClickAction = () => {
    setPopperOpen(false);
    if (isPendingReviewFilterActive) {
      onClearPendingReviewFilter();
      return;
    }
    onViewPendingReview();
  };

  return (
    <>
      <Badge badgeContent={1} color="primary">
        <AlineTooltip arrow placement="top" title="Alerts">
          <IconButton
            ref={setAnchorEl}
            aria-label="Alerts"
            className={cx(
              classes.alertsButton,
              (popperOpen || isPendingReviewFilterActive) && classes.alertsButtonActive
            )}
            size="small"
            onClick={() => setPopperOpen(!popperOpen)}
          >
            <Info size={18} />
          </IconButton>
        </AlineTooltip>
      </Badge>
      <Popper
        anchorEl={anchorEl}
        className={classes.popper}
        open={popperOpen}
        placement="bottom-end"
      >
        <Paper ref={anchorRef} className={classes.paper}>
          <Typography className={classes.paperHeader} variant="subtitle1">
            Alerts
          </Typography>
          <div className={classes.alertRow}>
            <span className={classes.alertIcon}>
              <Bot size={20} />
            </span>
            <div className={classes.alertRowInner}>
              <Typography noWrap className={classes.alertTitle}>
                {pendingReviewCount} AI {pendingReviewCount === 1 ? 'reminder' : 'reminders'}{' '}
                pending review
              </Typography>
              <Typography className={classes.subtitle}>
                Review AI-suggested reminders before they become active
              </Typography>
            </div>
            <Button
              className={classes.viewAllButton}
              color="primary"
              size="small"
              variant="outlined"
              onClick={handleClickAction}
            >
              {isPendingReviewFilterActive ? 'Clear' : 'View'}
            </Button>
          </div>
        </Paper>
      </Popper>
    </>
  );
}

export function AddReminder() {
  const navigate = useNavigate();
  const { classes } = useStyles();
  const currentUser = useSelectCurrentUser();
  const { openDrawer } = useGlobalDrawer();
  const [dialogOpen, setDialogOpen] = useState(false);
  const handleOpenDialog = useAccessGuard({
    callback: () => setDialogOpen(true),
    minRole: Apollo.OrgRole.Basic,
  });
  const { createReminder, loading: creating } = useCreateReminder();
  const [selectedDocumentId, setSelectedDocumentId] = useState<UUID | null>(null);
  const dispatchError = useDispatchNotification({
    message: 'Failed to create reminder. Please try again.',
    variant: 'error',
  });

  async function handleCreate() {
    if (!selectedDocumentId) return;
    try {
      const { data } = await createReminder({
        variables: {
          input: {
            documentId: selectedDocumentId,
            props: {},
          },
        },
      });
      if (!data?.createReminder.id) return;

      handleClose();
      navigate(link.RemindersPreview({ reminderId: data.createReminder.id }));
      openDrawer();
    } catch {
      dispatchError();
    }
  }

  function handleClose() {
    setSelectedDocumentId(null);
    setDialogOpen(false);
  }

  return (
    <>
      <PrimaryPlusIconButton tooltip="New Reminder" onClick={handleOpenDialog} />
      {currentUser?.myRepositoryId && (
        <AlineDialog
          classes={{ paperWidthMd: classes.dialogPaper }}
          open={dialogOpen}
          title="Select a document for the reminder"
          onClose={handleClose}
        >
          <div className={classes.dialogPickerWrapper}>
            <DocumentPicker
              showDocumentCountFooter
              showFiltersButton
              documentVariant={Apollo.DocumentVariant.Document}
              pickerVariant={DocumentPickerVariant.SingleSelect}
              repositoryId={currentUser.myRepositoryId}
              onSelectionChange={ids => setSelectedDocumentId(ids.at(0) ?? null)}
            />
          </div>
          <AlineDialogButtons
            cancelHandler={handleClose}
            className={classes.dialogButtons}
            disabled={!selectedDocumentId}
            loading={creating}
            submitHandler={handleCreate}
            submitText="Create"
          />
        </AlineDialog>
      )}
    </>
  );
}

function ReminderTypePicker({ remindersStateTuple }: RemindersToolbarProps) {
  const { classes } = useStyles();
  const anchorRef = useRef<HTMLDivElement>(null);
  const [isPopperOpen, setIsPopperOpen] = useState(false);
  const [remindersState, setRemindersState] = remindersStateTuple ?? [null, doNothing];
  const filterTypes = remindersState?.filterTypes ?? [];
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const selectedTypes = new Set(filterTypes);

  useClickAway(anchorRef, () => setIsPopperOpen(false));

  return (
    <>
      <Badge
        badgeContent={filterTypes.length}
        color="primary"
        sx={{ '& .MuiBadge-badge': { right: 2, top: 2 } }}
      >
        <DropdownSelectButton
          ref={setAnchorEl}
          icon={<Tag size={18} />}
          label="Type"
          selected={isPopperOpen}
          variant="outlined"
          onClick={() => setIsPopperOpen(!isPopperOpen)}
        />
      </Badge>
      <Popper
        anchorEl={anchorEl}
        className={classes.popper}
        open={isPopperOpen}
        placement="bottom-start"
      >
        <Paper ref={anchorRef} className={classes.paper}>
          <ReminderTypeChooser
            isPopperOpen={isPopperOpen}
            selectedTypes={filterTypes}
            onChange={(_, value) => {
              if (!value) return;

              const isAlreadyThere = selectedTypes.has(value);
              setRemindersState(s => ({
                ...s,
                filterTypes: isAlreadyThere
                  ? filterTypes.filter(t => t !== value)
                  : [...filterTypes, value],
              }));
            }}
          />
        </Paper>
      </Popper>
    </>
  );
}

function RemindersAssigneesPicker({ remindersStateTuple }: RemindersToolbarProps) {
  const { classes } = useStyles();
  const anchorRef = useRef<HTMLDivElement>(null);
  const [isPopperOpen, setIsPopperOpen] = useState(false);
  const [remindersState, setRemindersState] = remindersStateTuple ?? [null, doNothing];
  const filterAssignees = remindersState?.filterAssignees ?? [];
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  useClickAway(anchorRef, () => setIsPopperOpen(false));

  return (
    <>
      <Badge
        badgeContent={filterAssignees.length}
        color="primary"
        sx={{ '& .MuiBadge-badge': { right: 2, top: 2 } }}
      >
        <DropdownSelectButton
          ref={setAnchorEl}
          icon={<Users size={18} />}
          label="Assignees"
          selected={isPopperOpen}
          variant="outlined"
          onClick={() => setIsPopperOpen(!isPopperOpen)}
        />
      </Badge>
      <Popper
        anchorEl={anchorEl}
        className={classes.popper}
        open={isPopperOpen}
        placement="bottom-start"
      >
        <Paper ref={anchorRef} className={classes.assigneesPaper}>
          <UserAutocomplete
            alwaysOpen
            includeUnassigned
            maxChips={4}
            placeholder="Filter assignees"
            value={filterAssignees}
            onChange={users => setRemindersState(s => ({ ...s, filterAssignees: users }))}
          />
        </Paper>
      </Popper>
    </>
  );
}
